import React, { useLayoutEffect, useEffect } from 'react';
import { connect } from 'react-redux';
import { browserHistory } from '@americanexpress/one-app-router';
import { compose, bindActionCreators } from 'redux';
import { connectAsync } from 'iguazu';
import { queryLanguagePack } from '@americanexpress/one-app-ducks';
import {
  getSelectedProduct,
  getProductsByTypes,
  CARD_PRODUCT,
  getProductAccountKey,
  getProductRegistryById,
} from '@americanexpress/product-ducks';
import { Selectors, generateUrls } from '@americanexpress/axp-benefits-utils';
import { fromJS } from 'immutable';
import { redirectToMycaLogin } from '../new/view-all/utils';
import EnrollRouter from '../new/enroll/components/EnrollRouter';
import { Loading } from '../new/shared/components/loading';
import isDev from '../utilities/is-dev';
import useOneData from '../hooks/useOneData';
import { getEndpoint, getReadBenefitsRequestBody } from '../api/api-utils';
import { ReadLoyaltyBenefitsVx } from '../constants';
import { getData } from '../new/view-all/selectors';
import { fetchCardMemberInfo, clearCardMemberInfo } from '../new/view-all/ducks/member-info';
import { extractHTMLInnerText } from '../utilities/extract-html-inner-text.js';

export function EnrollWrapper(props) {
  const membership = props?.membership;
  const MYCA_LOGIN_URL = props?.MYCA_LOGIN_URL;
  const parameters = props?.params || {};
  const benefitNameFromParam = parameters?.benname;
  const cardNameState = props?.cardNameState;
  const cardNameParams = parameters?.cardname;
  const location = props?.location;
  const languageData = props?.languageData;
  const pathname = location?.pathname.toLowerCase();

  const getCardNameFromState = cardNameState?.data?.cardDetails?.cardName;
  const cardNameValueToUse = getCardNameFromState || cardNameParams;
  const accountToken = props?.accountToken;

  const faasApiUrl = props?.faasApiUrl;
  const endpoint = getEndpoint(faasApiUrl, ReadLoyaltyBenefitsVx);
  const login = props?.login;
  const productType = props?.productType;
  const locale = props?.locale;

  const requestBody = getReadBenefitsRequestBody(accountToken, cardNameValueToUse, login, productType, locale);
  // const { isFetching, error, data } = props?.data;

  const endpointOptions = {
    body: requestBody,
  };

  const {
    isLoading: loadingReadLoyaltyBenefits,
    data: readLoyaltyBenefits,
    error: readLoyaltyBenefitsError,
  } = useOneData(endpoint, endpointOptions);

  useEffect(() => {
    props?.fetchCardMemberInfo();
  }, []);

  /**
   * An unauthenticated user will redirect to login and back
   */

  // BUG: domain is undefined
  useLayoutEffect(() => {
    const missingAccountToken = !accountToken;
    const destPage = pathname;
    if (missingAccountToken && global.BROWSER) {
      if (isDev) {
        console.log('Enrollment page will redirect to login page');
      }
      redirectToMycaLogin(MYCA_LOGIN_URL, destPage);
    }
  }, [accountToken]);

  /**
   * Redirect user to details page if status is NOT one of the following:
   *  NOTENROLLED, CANCELLED, OPENWINDOW
   */
  useLayoutEffect(() => {
    const hasDataForBenefitNameFromParam = readLoyaltyBenefits?.benefits[benefitNameFromParam];
    const detailsPageURL = `/card-benefits/detail/${benefitNameFromParam}/${cardNameParams}`;

    if (hasDataForBenefitNameFromParam) {
      const isStatusNotEnrolled = readLoyaltyBenefits?.benefits[benefitNameFromParam]?.enrollmentApplication?.enrollmentStatus === 'NOTENROLLED';
      const isStatusREJECTED = readLoyaltyBenefits?.benefits[benefitNameFromParam]?.enrollmentApplication?.enrollmentStatus === 'REJECTED';
      const isStatusCancelled = readLoyaltyBenefits?.benefits[benefitNameFromParam]?.enrollmentApplication?.enrollmentStatus === 'CANCELLED';
      const isStatusOpenWindow = readLoyaltyBenefits?.benefits[benefitNameFromParam]?.layoutType === 'OPENWINDOW';
      const isStatusNOTENROLLED = readLoyaltyBenefits?.benefits[benefitNameFromParam]?.layoutType === 'NOTENROLLED';
      const isBenefitEnrollable = readLoyaltyBenefits?.benefits[benefitNameFromParam].isEnrollable;
      const userCanEnroll = isStatusNotEnrolled || isStatusCancelled || isStatusOpenWindow || isStatusREJECTED || isStatusNOTENROLLED;

      if (!userCanEnroll || !isBenefitEnrollable) {
        /* istanbul ignore next */
        if (isDev) {
          // TODO: Trigger a beacon call to track scenario.
          console.log(`Enrollment page will redirect to ${detailsPageURL} because enrollment status is NOT the following: CANCELLED, NOTENROLLED or benefit is not enrollable`);
        }
        browserHistory.push(detailsPageURL);
      }
    }
  }, [benefitNameFromParam, readLoyaltyBenefits]);

  if (loadingReadLoyaltyBenefits) {
    return <Loading data-testid="loading-component" />;
  }

  const benefitData = readLoyaltyBenefits?.benefits[benefitNameFromParam];
  const enrollmentData = benefitData?.enrollment;

  const initialEnrollmentStepId = enrollmentData?.steps?.initial;
  const enrollmentStepId = enrollmentData?.steps[initialEnrollmentStepId]?.id;
  const enrollmentContent = enrollmentData?.content[enrollmentStepId];

  const backHeaderData = {
    header: enrollmentContent?.header,
    url: '/card-benefits/view-all',
  };

  const benTitle = readLoyaltyBenefits?.benefits[parameters?.benname]?.benefitTitle;
  const cardNameFull = readLoyaltyBenefits?.cardProduct?.productLayout?.pageTitle.split('|')[0].trim();
  const cleanBenTitle = benTitle ? extractHTMLInnerText(benTitle.replace('TM', '&#8482;')) : {};

  return (
    <article data-testid="enroll-page">
      <EnrollRouter
        {...props}
        loadingReadLoyaltyBenefits={loadingReadLoyaltyBenefits}
        readLoyaltyBenefits={benefitData}
        readLoyaltyBenefitsError={readLoyaltyBenefitsError}
        cardname={cardNameParams}
        benname={benefitNameFromParam}
        location={location}
        faasApiUrl={faasApiUrl}
        selectedProduct={accountToken}
        accountKey={props?.accountKey}
        membership={membership}
        languageData={languageData}
        cardData={props?.data}
        backHeaderData={backHeaderData}
        cardNameFull={cardNameFull}
        cleanBenTitle={cleanBenTitle}
      />
    </article>
  );
}

export function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchCardMemberInfo,
    clearCardMemberInfo,
  }, dispatch);
}

export function mapStateToProps(state) {
  const product = getSelectedProduct(state);
  const products = getProductsByTypes(state, [CARD_PRODUCT]);
  const env = fromJS(state).getIn(['config', 'localeFilename']);
  return {
    cardNameState: Selectors.getCardNameFromState(state),
    faasApiUrl: fromJS(state).getIn(['config', 'faasApiUrl']),
    accountToken: product,
    accountKey: getProductAccountKey(state),
    MYCA_LOGIN_URL: generateUrls(env).MYCA_LOGIN_URL,
    membership: products?.[CARD_PRODUCT]?.productsList?.[product]?.account?.relationship || null,
    data: getData(state),
    login: Boolean(getSelectedProduct(state)),
    productType: getProductRegistryById(state, product)?.type,
    locale: fromJS(state).getIn(['intl', 'activeLocale']),
  };
}

export const loadDataAsProps = ({ store: { dispatch } }) => {
  return {
    languageData: () => dispatch(queryLanguagePack('axp-benefits-view-all', { fallbackLocale: 'en-US' })),
  };
};

const hocChain = compose(
  connect(mapStateToProps, mapDispatchToProps),
  connectAsync({ loadDataAsProps })
);

export const ComponentUnderTest = EnrollWrapper;
export default hocChain(EnrollWrapper);
